import { Button, Toggle, Tooltip } from "@gotts.ai/ui";
import { useState } from "react";
import { type Address, formatUnits } from "viem";

import { TokenIcon } from "../../components/TokenIcon";
import { useDeployment } from "../../context";
import { DEVENV_TOKEN_PRICES, TOKEN_DECIMALS } from "../../lib/constants";
import { formatUsd } from "../../lib/format";

import { resolveSymbol, type V2Position } from "./types";

interface V2PositionsTableProps {
  positions: V2Position[];
  loading: boolean;
  onRemove?: (position: V2Position) => void;
}

function fmt(raw: bigint, symbol: string): string {
  const decimals = TOKEN_DECIMALS[symbol] ?? 18;
  const n = Number(formatUnits(raw, decimals));
  if (n === 0) return "0";
  if (n < 0.0001) return "<0.0001";
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

export function V2PositionsTable({
  positions,
  loading,
  onRemove,
}: V2PositionsTableProps) {
  const { deployment } = useDeployment();
  const tokens = (deployment?.tokens ?? {}) as unknown as Record<
    string,
    Address
  >;
  const [showEmpty, setShowEmpty] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filteredPositions = showEmpty
    ? positions
    : positions.filter((p) => p.lpBalance > 0n);

  if (loading && positions.length === 0) {
    return (
      <div className="space-y-3">
        <div className="skeleton skeleton-row rounded" />
        <div className="skeleton skeleton-row rounded" />
        <div className="skeleton skeleton-row rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="lp-info-banner">
        <svg
          className="lp-info-banner-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        V2 positions are full-range (constant product x*y=k). Fees are
        auto-compounded into your LP tokens.
      </div>

      <Toggle
        checked={showEmpty}
        onCheckedChange={setShowEmpty}
        label="Include zero-balance positions"
      />

      {filteredPositions.length === 0 && (
        <p className="type-supporting py-4 text-center">
          No V2 positions found
        </p>
      )}

      <div className="space-y-3">
        {filteredPositions.map((pos) => {
          const sym0 = resolveSymbol(pos.token0, tokens);
          const sym1 = resolveSymbol(pos.token1, tokens);
          const dec0 = TOKEN_DECIMALS[sym0] ?? 18;
          const dec1 = TOKEN_DECIMALS[sym1] ?? 18;
          const hasBalance = pos.lpBalance > 0n;
          const usd0 =
            (Number(pos.userShare0) / 10 ** dec0) *
            (DEVENV_TOKEN_PRICES[sym0] ?? 0);
          const usd1 =
            (Number(pos.userShare1) / 10 ** dec1) *
            (DEVENV_TOKEN_PRICES[sym1] ?? 0);
          const totalUsd = usd0 + usd1;
          const pct0 = totalUsd > 0 ? (usd0 / totalUsd) * 100 : 50;
          const isExpanded = expandedId === pos.pairAddress;

          return (
            <div
              key={pos.pairAddress}
              className={`lp-card lp-card-v2 ${!hasBalance ? "lp-card-zero" : ""}`}
            >
              {/* Header: icons + pair name + badge + hero USD */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="lp-pair-icons">
                    <TokenIcon symbol={sym0} size={32} />
                    <TokenIcon symbol={sym1} size={32} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-mono text-sm font-semibold text-[var(--color-gold)]">
                      {pos.pair}
                    </span>
                    <div className="mt-0.5 flex items-center gap-2">
                      <span
                        className="protocol-pill protocol-pill-v2"
                        style={{ fontSize: 9, padding: "1px 6px" }}
                      >
                        V2
                      </span>
                      <span
                        className="badge-position-type"
                        style={{ fontSize: 9, padding: "1px 6px" }}
                      >
                        Full Range
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <span className="hero-value" style={{ fontSize: 22 }}>
                    {totalUsd > 0 ? formatUsd(totalUsd) : "$0.00"}
                  </span>
                </div>
              </div>

              {/* Token breakdown rows */}
              <div className="mt-4 space-y-2">
                <div className="lp-token-row">
                  <div className="flex items-center gap-2">
                    <TokenIcon symbol={sym0} size={18} />
                    <span className="font-mono text-xs font-medium text-[var(--color-text)]">
                      {sym0}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-xs text-[var(--color-text)]">
                      {fmt(pos.userShare0, sym0)}
                    </span>
                    <span className="ml-2 font-mono text-[10px] text-[var(--color-text-muted)]">
                      {"\u2248"} {formatUsd(usd0)}
                    </span>
                  </div>
                </div>
                <div className="lp-token-row">
                  <div className="flex items-center gap-2">
                    <TokenIcon symbol={sym1} size={18} />
                    <span className="font-mono text-xs font-medium text-[var(--color-text)]">
                      {sym1}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-xs text-[var(--color-text)]">
                      {fmt(pos.userShare1, sym1)}
                    </span>
                    <span className="ml-2 font-mono text-[10px] text-[var(--color-text-muted)]">
                      {"\u2248"} {formatUsd(usd1)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Allocation bar */}
              {totalUsd > 0 && (
                <div className="mt-4">
                  <div className="allocation-bar">
                    <div
                      className="allocation-bar-segment allocation-bar-token0"
                      style={{ width: `${String(pct0)}%` }}
                    />
                    <div
                      className="allocation-bar-segment allocation-bar-token1"
                      style={{ width: `${String(100 - pct0)}%` }}
                    />
                  </div>
                  <div className="mt-1.5 flex justify-between text-[10px] text-[var(--color-text-muted)]">
                    <span className="flex items-center gap-1">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-[rgba(79,143,234,0.6)]" />
                      {pct0.toFixed(0)}% {sym0}
                    </span>
                    <span className="flex items-center gap-1">
                      {(100 - pct0).toFixed(0)}% {sym1}
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-[rgba(201,168,76,0.6)]" />
                    </span>
                  </div>
                </div>
              )}

              {/* Actions + details toggle */}
              <div className="lp-actions">
                <button
                  type="button"
                  className="btn-ghost rounded-sm px-3 text-[10px]"
                  onClick={() => {
                    setExpandedId(isExpanded ? null : pos.pairAddress);
                  }}
                >
                  {isExpanded ? "Hide Details" : "Details"}
                </button>
                <div className="flex-1" />
                {hasBalance && onRemove ? (
                  <button
                    type="button"
                    className="btn-remove-destructive"
                    onClick={() => {
                      onRemove(pos);
                    }}
                  >
                    Remove
                  </button>
                ) : (
                  <Tooltip content="No LP tokens held">
                    <span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="opacity-40"
                      >
                        Remove
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </div>

              {/* Expandable details */}
              <div
                className="lp-detail-expand"
                data-open={isExpanded ? "true" : "false"}
              >
                <div>
                  <div className="lp-detail-grid">
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Pair Address</span>
                      <span className="lp-detail-item-value">
                        {pos.pairAddress}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">LP Balance</span>
                      <span className="lp-detail-item-value">
                        {pos.lpBalance.toString()}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Token 0</span>
                      <span className="lp-detail-item-value">{pos.token0}</span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Token 1</span>
                      <span className="lp-detail-item-value">{pos.token1}</span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Reserve 0</span>
                      <span className="lp-detail-item-value">
                        {pos.userShare0.toString()}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Reserve 1</span>
                      <span className="lp-detail-item-value">
                        {pos.userShare1.toString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
